import type { LocationQuery, LocationQueryRaw } from "vue-router";

import type { MerchantApiClient } from "../../types.js";
import { compactQuery, readNumberQuery, readStringQuery } from "../../shared/view-helpers.js";
import { MISSING_VALUE_LABEL } from "../../shared/display-labels.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createProductsApi, type ProductsApi } from "./api.js";
import {
  formatProductCount,
  formatProductDate,
  productStatusTone,
  productSyncTone,
} from "./formatters.js";
import { productLastSyncedTone } from "./product-sync-rules.js";
import type {
  BulkEligibilityResult,
  BulkEligibilityUpdate,
  EligibilityDraft,
  MerchantJobStatusDto,
  MerchantProductsListResponseData,
  ProductDetailResponse,
  ProductFilters,
  ProductListActionsViewModel,
  ProductListFiltersViewModel,
  ProductListPageViewModel,
  ProductListRowDto,
  ProductListRowViewModel,
  ProductPaginationViewModel,
  ProductRow,
  ProductSectionStateViewModel,
  ProductSummaryCardViewModel,
  ProductSyncViewModel,
  ProductWarningViewModel,
} from "./models.js";

export interface ProductsTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface ProductsService {
  bulkUpdateEligibility(updates: BulkEligibilityUpdate[]): Promise<BulkEligibilityResult>;
  filtersFromRouteQuery(query: LocationQuery): ProductListFiltersViewModel;
  loadJob(jobId: string): Promise<MerchantJobStatusDto>;
  loadProduct(productId: string): Promise<ProductDetailResponse>;
  loadProducts(
    filters: ProductFilters,
    draftFilters?: ProductListFiltersViewModel,
    selectedCount?: number,
  ): Promise<ProductListPageViewModel>;
  routeQueryFromFilters(filters: ProductListFiltersViewModel): LocationQueryRaw;
  startSync(trigger: "MANUAL" | "RETRY"): Promise<{ job: MerchantJobStatusDto; queued: boolean }>;
  updateEligibility(productId: string, draft: EligibilityDraft): Promise<ProductRow>;
}

export interface ProductsServiceOptions {
  api?: ProductsApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: ProductsTelemetry;
}

export function createProductsService(options: ProductsServiceOptions): ProductsService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  return {
    async bulkUpdateEligibility(updates) {
      return track(
        telemetry,
        "merchant_products_bulk_eligibility",
        () => api.bulkUpdateEligibility(updates, idempotencyKeyFactory()),
        { count: updates.length },
      );
    },
    filtersFromRouteQuery(query) {
      return filtersFromRouteQuery(query);
    },
    async loadJob(jobId) {
      return track(telemetry, "merchant_product_sync_job_loaded", () => api.getJob(jobId), {
        jobId,
      });
    },
    async loadProduct(productId) {
      return track(telemetry, "merchant_product_detail_loaded", () => api.getProduct(productId), {
        productId,
      });
    },
    async loadProducts(filters, draftFilters, selectedCount) {
      const data = await track(
        telemetry,
        "merchant_products_loaded",
        () => api.listProducts(filters),
        { status: filters.status, sort: filters.sort },
      );
      return toProductListPageViewModel(
        data,
        draftFilters ?? toFilterViewModel(filters),
        selectedCount ?? 0,
      );
    },
    routeQueryFromFilters(filters) {
      return routeQueryFromFilters(filters);
    },
    async startSync(trigger) {
      return track(
        telemetry,
        "merchant_product_sync_started",
        () => api.startSync(trigger, idempotencyKeyFactory()),
        { trigger },
      );
    },
    async updateEligibility(productId, draft) {
      return track(
        telemetry,
        "merchant_product_eligibility_updated",
        () => api.updateEligibility(productId, draft, idempotencyKeyFactory()),
        { productId, eligible: draft.eligible },
      );
    },
  };
}

export function defaultProductListFilters(): ProductListFiltersViewModel {
  return {
    search: "",
    searchMode: "PREFIX",
    status: "",
    eligibility: "",
    availability: "",
    mapping: "",
    attention: "",
    sort: "UPDATED_DESC",
    limit: 25,
  };
}

export function toProductFilters(filters: ProductListFiltersViewModel): ProductFilters {
  return {
    limit: filters.limit,
    searchMode: filters.searchMode,
    sort: filters.sort,
    ...(filters.status === "" ? {} : { status: filters.status }),
    ...(filters.search.trim().length === 0 ? {} : { search: filters.search.trim() }),
    ...(filters.eligibility === "" ? {} : { eligibility: filters.eligibility }),
    ...(filters.availability === "" ? {} : { availability: filters.availability }),
    ...(filters.mapping === "" ? {} : { mapping: filters.mapping }),
    ...(filters.attention === "" ? {} : { attention: filters.attention }),
    ...(filters.cursor === undefined ? {} : { cursor: filters.cursor }),
  };
}

function filtersFromRouteQuery(query: LocationQuery): ProductListFiltersViewModel {
  const defaults = defaultProductListFilters();
  const cursor = readStringQuery(query, "cursor");
  return {
    search: readStringQuery(query, "search") ?? defaults.search,
    searchMode: readEnum(
      readStringQuery(query, "searchMode"),
      ["PREFIX", "EXACT"],
      defaults.searchMode,
    ),
    status: readEnum(
      readStringQuery(query, "status"),
      ["", "ACTIVE", "DRAFT", "ARCHIVED", "DELETED"],
      defaults.status,
    ),
    eligibility: readEnum(
      readStringQuery(query, "eligibility"),
      ["", "ELIGIBLE", "NOT_ELIGIBLE"],
      defaults.eligibility,
    ),
    availability: readEnum(
      readStringQuery(query, "availability"),
      ["", "IN_STOCK", "LOW_STOCK", "OUT_OF_STOCK", "UNKNOWN"],
      defaults.availability,
    ),
    mapping: readEnum(
      readStringQuery(query, "mapping"),
      ["", "HAS_MAPPING", "NO_MAPPING", "STALE_MAPPING"],
      defaults.mapping,
    ),
    attention: readEnum(
      readStringQuery(query, "attention"),
      ["", "MISSING_IDENTIFIERS", "MISSING_COST", "STALE_MAPPINGS"],
      defaults.attention,
    ),
    sort: readEnum(
      readStringQuery(query, "sort"),
      ["UPDATED_DESC", "TITLE_ASC", "TITLE_DESC", "NEEDS_ATTENTION_DESC"],
      defaults.sort,
    ),
    limit: normalizeLimit(readNumberQuery(query, "limit")),
    ...(cursor === undefined ? {} : { cursor }),
  };
}

function routeQueryFromFilters(filters: ProductListFiltersViewModel): LocationQueryRaw {
  return compactQuery({
    search: filters.search.trim(),
    searchMode: filters.searchMode === "PREFIX" ? undefined : filters.searchMode,
    status: filters.status,
    eligibility: filters.eligibility,
    availability: filters.availability,
    mapping: filters.mapping,
    attention: filters.attention,
    sort: filters.sort === "UPDATED_DESC" ? undefined : filters.sort,
    limit: filters.limit === 25 ? undefined : filters.limit,
    cursor: filters.cursor,
  });
}

function toProductListPageViewModel(
  data: MerchantProductsListResponseData,
  draftFilters: ProductListFiltersViewModel,
  selectedCount: number,
): ProductListPageViewModel {
  const appliedFilters = toFilterViewModel({
    search: data.filters.search ?? undefined,
    searchMode: data.filters.searchMode,
    status: data.filters.status ?? "",
    eligibility: data.filters.eligibility ?? "",
    availability: data.filters.availability ?? "",
    mapping: data.filters.mapping ?? "",
    attention: data.filters.attention ?? "",
    sort: data.filters.sort,
    limit: data.filters.limit,
    cursor: data.pagination.currentCursor ?? undefined,
  });
  return {
    appliedFilters,
    draftFilters,
    summaryCards: toSummaryCards(data),
    sync: toSyncViewModel(data),
    rows: data.items.map(toRowViewModel),
    warnings: data.warnings.map(toWarningViewModel),
    pagination: toPaginationViewModel(data),
    actions: toListActionsViewModel(data, selectedCount),
    sectionStates: toSectionStates(data),
  };
}

function toFilterViewModel(filters: ProductFilters): ProductListFiltersViewModel {
  return {
    ...defaultProductListFilters(),
    search: filters.search ?? "",
    searchMode: filters.searchMode ?? "PREFIX",
    status: filters.status ?? "",
    eligibility: filters.eligibility ?? "",
    availability: filters.availability ?? "",
    mapping: filters.mapping ?? "",
    attention: filters.attention ?? "",
    sort: filters.sort ?? "UPDATED_DESC",
    limit: filters.limit ?? 25,
    ...(filters.cursor === undefined ? {} : { cursor: filters.cursor }),
  };
}

function toSummaryCards(data: MerchantProductsListResponseData): ProductSummaryCardViewModel[] {
  return [
    {
      id: "catalogProducts",
      label: "Catalog products",
      valueText: formatProductCount(data.summary.catalogProductCount),
      detailText: `${formatProductCount(data.summary.activeProductCount)} active`,
      helpText:
        "Total products imported from your commerce platform catalog. The detail shows how many of those products are currently active.",
      tone: "neutral",
    },
    {
      id: "eligibleProducts",
      label: "Eligible",
      valueText: formatProductCount(data.summary.eligibleProductCount),
      detailText: "Ready for widget",
      helpText:
        "Products that can use Price Rescue based on current eligibility rules, catalog data, and required product information.",
      tone: "good",
    },
    {
      id: "needsAttention",
      label: "Needs attention",
      valueText: formatProductCount(data.summary.needsAttentionCount),
      detailText: "Identifiers, cost or mappings",
      helpText:
        "Products that may need merchant review before Price Rescue can safely use them, such as missing identifiers, missing cost data, or stale competitor mappings.",
      tone: data.summary.needsAttentionCount > 0 ? "warning" : "good",
    },
    {
      id: "lastSynced",
      label: "Last synced",
      valueText:
        data.sync.lastSuccessAt === null
          ? "Not synced"
          : formatProductDate(data.sync.lastSuccessAt),
      detailText: "",
      tone: productLastSyncedTone(data.sync),
    },
  ];
}

function toSyncViewModel(data: MerchantProductsListResponseData): ProductSyncViewModel {
  return {
    label: syncLabel(data.sync.status),
    detailText: data.sync.lastErrorSummary ?? syncDetail(data.sync),
    status: data.sync.status,
    ...(data.sync.activeJobId === null
      ? {}
      : { viewJobHref: `/jobs/${encodeURIComponent(data.sync.activeJobId)}` }),
    tone: productSyncTone(data.sync.status),
  };
}

function toRowViewModel(row: ProductListRowDto): ProductListRowViewModel {
  return {
    productId: row.productId,
    title: row.title,
    ...(row.imageUrl === null ? {} : { imageUrl: row.imageUrl }),
    categoryLabel: row.handle,
    platformStatusLabel: row.platformStatus,
    platformStatusTone: productStatusTone(row.platformStatus),
    eligibilityLabel: row.eligibility.label,
    eligibilityTone: row.eligibility.status === "ELIGIBLE" ? "good" : "warning",
    ...(row.eligibility.status === "ELIGIBLE" || row.eligibility.reasonLabel === null
      ? {}
      : { eligibilityReasonLabel: row.eligibility.reasonLabel }),
    ...(row.eligibility.merchantNote === null
      ? {}
      : { eligibilityNote: row.eligibility.merchantNote }),
    policy: toPolicyViewModel(row.policy),
    availabilityLabel: row.availability.label,
    inventoryLabel: row.inventory.label,
    trackingLabel: row.inventory.tracked ? "Tracked" : "Tracking off",
    trackingTone: row.inventory.tracked ? "good" : "warning",
    vendorLabel: row.vendor ?? MISSING_VALUE_LABEL,
    productTypeLabel: row.productType ?? MISSING_VALUE_LABEL,
    identifiersLabel: row.identifiers.label,
    ...(row.identifiers.missingRequiredCount === 0
      ? {}
      : { identifiersReasonLabel: identifierReasonLabel(row.identifiers.missingRequiredCount) }),
    costLabel: row.cost.hasRequiredCost ? row.cost.label : MISSING_VALUE_LABEL,
    ...(row.cost.hasRequiredCost
      ? {}
      : { costReasonLabel: costReasonLabel(row.cost.missingRequiredCostCount) }),
    mapping: toMappingViewModel(row.mapping),
    mappingLabel: row.mapping.label,
    monitoringLabel: row.monitoring.label,
    viewHref: `/products/${encodeURIComponent(row.productId)}`,
    actions: [
      {
        id: "view",
        label: "View",
        href: `/products/${encodeURIComponent(row.productId)}`,
        enabled: row.actions.view.enabled,
        ...(row.actions.view.reason === null ? {} : { disabledReason: row.actions.view.reason }),
      },
      {
        id: "viewOnStore",
        label: "View on store",
        ...(row.actions.viewOnStore.href === null ? {} : { href: row.actions.viewOnStore.href }),
        enabled: row.actions.viewOnStore.enabled,
        ...(row.actions.viewOnStore.reason === null
          ? {}
          : { disabledReason: row.actions.viewOnStore.reason }),
      },
      {
        id: "editEligibility",
        label: "Edit eligibility",
        enabled: row.actions.editEligibility.enabled,
        ...(row.actions.editEligibility.reason === null
          ? {}
          : { disabledReason: row.actions.editEligibility.reason }),
      },
      {
        id: "runCheck",
        label: "Run check",
        enabled: row.actions.runCheck.enabled,
        ...(row.actions.runCheck.reason === null
          ? {}
          : { disabledReason: row.actions.runCheck.reason }),
      },
    ],
  };
}

function toPolicyViewModel(
  rowPolicy: ProductListRowDto["policy"],
): ProductListRowViewModel["policy"] {
  return {
    source: rowPolicy.source,
    label: rowPolicy.label,
  };
}

function toMappingViewModel(
  rowMapping: ProductListRowDto["mapping"],
): ProductListRowViewModel["mapping"] {
  return {
    status: rowMapping.status,
    mappedCount: rowMapping.mappedCount,
    staleCount: rowMapping.staleCount,
    label: rowMapping.label,
  };
}

export function productEligibilityTooltip(
  product: Pick<ProductListRowViewModel, "eligibilityNote" | "eligibilityReasonLabel">,
): string | undefined {
  if (product.eligibilityNote !== undefined && product.eligibilityNote.trim().length > 0) {
    return product.eligibilityNote;
  }

  if (product.eligibilityReasonLabel === undefined) {
    return undefined;
  }

  return friendlyEligibilityReason(product.eligibilityReasonLabel);
}

function friendlyEligibilityReason(reasonLabel: string): string {
  if (reasonLabel === "Merchant Bulk Disabled") return "Marked ineligible by merchant.";
  if (reasonLabel === "Merchant Bulk Enabled") return "Marked eligible by merchant.";
  return reasonLabel;
}

function identifierReasonLabel(missingCount: number): string {
  return missingCount === 1
    ? "1 variant has no SKU, barcode/GTIN, or model/MPN identifier. Add at least one identifier to improve matching confidence."
    : `${missingCount} variants have no SKU, barcode/GTIN, or model/MPN identifier. Add at least one identifier to improve matching confidence.`;
}

function costReasonLabel(missingCount: number): string {
  return missingCount === 1
    ? "1 variant is missing cost data. Margin-aware offer decisions may require manual review."
    : `${missingCount} variants are missing cost data. Margin-aware offer decisions may require manual review.`;
}

function toWarningViewModel(
  warning: MerchantProductsListResponseData["warnings"][number],
): ProductWarningViewModel {
  return {
    code: warning.code,
    label: warning.code.toLowerCase().replaceAll("_", " "),
    ...(warning.count === null ? {} : { countText: formatProductCount(warning.count) }),
    message: warning.message,
    actionLabel: warningActionLabel(warning.code),
    reviewTarget: warning.code === "SYNC_FAILED" ? "SYNC_STATUS" : "FILTER",
  };
}

function warningActionLabel(code: string): string {
  if (code === "MISSING_IDENTIFIERS") return "Show missing identifiers";
  if (code === "MISSING_COST") return "Show missing cost";
  if (code === "STALE_MAPPINGS") return "Show stale mappings";
  if (code === "SYNC_FAILED") return "Review sync status";
  return "Review warning";
}

function toPaginationViewModel(data: MerchantProductsListResponseData): ProductPaginationViewModel {
  return {
    nextCursor: data.pagination.nextCursor,
    ...(data.pagination.currentCursor === null
      ? {}
      : { currentCursor: data.pagination.currentCursor }),
    canGoNext: data.pagination.nextCursor !== null,
    staleCursor: data.pagination.staleCursor,
  };
}

function toListActionsViewModel(
  data: MerchantProductsListResponseData,
  selectedCount: number,
): ProductListActionsViewModel {
  const bulkAllowed = data.allowedActions.bulkEligibilityUpdate.enabled;
  return {
    repairCatalogEnabled: data.allowedActions.startRepairSync.enabled,
    ...(data.allowedActions.startRepairSync.reason === null
      ? {}
      : { repairCatalogDisabledReason: data.allowedActions.startRepairSync.reason }),
    syncCatalogEnabled: data.allowedActions.startSync.enabled,
    ...(data.allowedActions.startSync.reason === null
      ? {}
      : { syncCatalogDisabledReason: data.allowedActions.startSync.reason }),
    bulkActionsEnabled: selectedCount > 0 && bulkAllowed,
    ...(selectedCount === 0
      ? { bulkActionsDisabledReason: "Select products to use bulk actions." }
      : data.allowedActions.bulkEligibilityUpdate.reason === null
        ? {}
        : { bulkActionsDisabledReason: data.allowedActions.bulkEligibilityUpdate.reason }),
    reviewWarningsEnabled: data.warnings.length > 0,
    ...(data.warnings.length > 0 ? { reviewWarningsHref: "#product-warnings" } : {}),
  };
}

function toSectionStates(data: MerchantProductsListResponseData): ProductSectionStateViewModel[] {
  return Object.entries(data.sections).map(([id, section]) => ({
    id,
    status: section.status === "UNAVAILABLE" ? "FAILED" : section.status,
    ...(section.message === null ? {} : { message: section.message }),
    requestId: section.requestId,
  }));
}

function syncLabel(status: MerchantProductsListResponseData["sync"]["status"]): string {
  if (status === "RUNNING" || status === "REPAIR_RUNNING") return "Catalog sync running";
  if (status === "FAILED") return "Catalog sync needs attention";
  if (status === "IDLE") return "Catalog sync ready";
  if (status === "PARTIAL") return "Catalog partially synced";
  return "Catalog sync not started";
}

function syncDetail(sync: MerchantProductsListResponseData["sync"]): string {
  if (sync.lastSuccessAt !== null)
    return `Last synced ${formatProductDate(sync.lastSuccessAt)}. Product and inventory data are shown from the latest platform sync.`;
  return "Run catalog sync to load products and inventory from your commerce platform.";
}

function readEnum<TValue extends string>(
  value: string | undefined,
  allowed: readonly TValue[],
  fallback: TValue,
): TValue {
  return value !== undefined && allowed.includes(value as TValue) ? (value as TValue) : fallback;
}

function normalizeLimit(value: number | undefined): number {
  if (value === undefined) return 25;
  return Math.min(Math.max(Math.floor(value), 1), 100);
}

async function track<T>(
  telemetry: ProductsTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): ProductsApi {
  if (client === undefined) {
    throw new Error("Products service requires an API client.");
  }

  return createProductsApi(client);
}

function browserTelemetry(): ProductsTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
